import { useEffect, useRef, useMemo, useState } from 'react';
import * as d3 from 'd3';

const BarChart = ({ data, height = 300 }) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const chartId = useMemo(() => `chart-${Math.random().toString(36).substring(2, 11)}`, []);
  const [containerWidth, setContainerWidth] = useState(0);

  // Handle container resizing
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      setContainerWidth(containerRef.current.clientWidth);
    };

    const resizeObserver = new ResizeObserver(entries => {
      if (!entries?.length) return;
      updateDimensions();
    });

    resizeObserver.observe(containerRef.current);
    updateDimensions();

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!data || !data.length || !svgRef.current || !containerWidth) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions with responsive width
    const margin = { top: 40, right: 30, bottom: 90, left: 60 };
    const width = containerWidth - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create root SVG with responsive viewBox
    const svg = d3.select(svgRef.current)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .attr("viewBox", `0 0 ${containerWidth} ${height}`)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Create scales
    const x = d3.scaleBand()
      .range([0, width])
      .domain(data.map(d => d.name))
      .padding(0.2);

    const y = d3.scaleLinear()
      .range([chartHeight, 0])
      .domain([0, d3.max(data, d => d.percentage) || 0])
      .nice();

    // Add X axis with responsive text sizing
    svg.append("g")
      .attr("transform", `translate(0,${chartHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end")
      .style("fill", "#1a1a1a")
      .style("font-weight", "600")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .style("font-size", `${Math.min(12, width / 50)}px`); // Responsive font size

    // Add Y axis
    svg.append("g")
      .call(d3.axisLeft(y)
        .ticks(5)
        .tickFormat(d => `${d}%`))
      .selectAll("text")
      .style("fill", "#1a1a1a")
      .style("font-weight", "600")
      .style("font-size", `${Math.min(12, width / 50)}px`);

    // Add Y axis label
    svg.append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", -margin.left + 20)
      .attr("x", -(chartHeight / 2))
      .attr("text-anchor", "middle")
      .style("fill", "#1a1a1a")
      .style("font-weight", "bold")
      .style("font-size", `${Math.min(12, width / 50)}px`)
      .text("Percentage");

    const colors = d3.scaleOrdinal()
      .domain(data.map(d => d.name))
      .range([
        "#FF6B6B", "#4ECDC4", "#45B7D1", 
        "#96CEB4", "#FFEEAD", "#D4A5A5",
        "#9B59B6", "#3498DB", "#E67E22"
      ]);

    // Create tooltip with fixed positioning
    const tooltipId = `tooltip-${chartId}`;
    const tooltip = d3.select('body')
      .append('div')
      .attr('class', 'project-dash-chart-tooltip')
      .attr('id', tooltipId)
      .style('position', 'fixed'); // Changed to fixed positioning

    // Add the bars with responsive handling
    svg.selectAll("rect")
      .data(data)
      .join("rect")
      .attr("x", d => x(d.name) || 0)
      .attr("width", x.bandwidth())
      .attr("y", chartHeight)
      .attr("height", 0)
      .attr("fill", d => colors(d.percentage))
      .on("mouseover", (event, d) => {
        const target = event.currentTarget;
        d3.select(target)
          .transition()
          .duration(100)
          .attr("opacity", 0.8);
        
        const rect = target.getBoundingClientRect();
        
        d3.select(`#${tooltipId}`)
          .style('display', 'block')
          .html(`
            <div class="tooltip-title">${d.name}</div>
            <div>${d.percentage.toFixed(1)}%</div>
          `);
      })
      .on("mousemove", (event) => {
        d3.select(`#${tooltipId}`)
          .style('left', `${event.clientX + 10}px`)
          .style('top', `${event.clientY - 10}px`);
      })
      .on("mouseout", (event) => {
        const target = event.currentTarget;
        d3.select(target)
          .transition()
          .duration(100)
          .attr("opacity", 1);
        d3.select(`#${tooltipId}`).style('display', 'none');
      })
      .transition()
      .duration(1000)
      .attr("y", d => y(d.percentage))
      .attr("height", d => chartHeight - y(d.percentage));

    // Add labels with responsive font size
    const labels = svg.selectAll(".bar-label")
      .data(data)
      .join("text")
      .attr("class", "bar-label")
      .attr("x", d => (x(d.name) || 0) + x.bandwidth() / 2)
      .attr("y", chartHeight - 0)
      .attr("text-anchor", "middle")
      .style("font-size", `${Math.min(12, width / 50)}px`)
      .style("fill", "#1a1a1a")
      .style("font-weight", "600")
      .text(d => d.total_data_points);

    labels.transition()
      .duration(1000)
      .attr("y", d => y(d.percentage) - 5);

    // Add grid lines
    svg.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(y)
        .ticks(5)
        .tickSize(-width)
        .tickFormat(() => '')
      )
      .style('stroke-dasharray', '3 3')
      .style('stroke-opacity', 0.2);

    // Cleanup
    return () => {
      d3.select(`#${tooltipId}`).remove();
    };
  }, [data, height, chartId, containerWidth]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        className="chart-svg"
      />
    </div>
  );
};

export default BarChart;